/**
 * Accepted image formats for uploads.
 *
 * Deliberately permissive: the server normalises anything browsers cannot
 * display (HEIC/HEIF from iPhones, TIFF, BMP, AVIF ...) into JPEG on the way
 * in, so a creator never has to convert a photo themselves. This module is
 * the single source of truth — the file picker, the validation and the
 * on-screen copy all read from it so they cannot drift apart.
 */

/** Extensions we take, including the ones browsers cannot preview locally. */
export const ACCEPTED_EXTENSIONS = [
  'jpg', 'jpeg', 'jfif',
  'png',
  'webp',
  'gif',
  'heic', 'heif', 'hif',
  'avif',
  'tif', 'tiff',
  'bmp',
] as const;

/**
 * `accept` for the file input.
 *
 * `image/*` alone is not enough: on Android and desktop a .heic often carries
 * no image mimetype, so it would be greyed out in the picker. The explicit
 * extension list puts it back.
 */
export const ACCEPT_ATTRIBUTE = [
  'image/*',
  ...ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

/** Human list for help text. Ordered by how often creators actually use them. */
export const ACCEPTED_FORMATS_LABEL = 'JPG, PNG, HEIC (iPhone), WebP, GIF, TIFF, BMP';

const EXTENSION_PATTERN = new RegExp(`\\.(${ACCEPTED_EXTENSIONS.join('|')})$`, 'i');

/**
 * Formats the browser itself can decode, for preview and local compression.
 * HEIC is excluded on purpose: only Safari can read it, so compressing it
 * client-side silently fails and previews come back blank.
 */
const BROWSER_READABLE = /^image\/(jpeg|jpg|png|webp|gif|avif)$/i;

/**
 * True when we will accept this file.
 *
 * Checks the extension as well as the mimetype, because iPhone photos
 * frequently arrive as 'application/octet-stream' or with no type at all.
 */
export const isAcceptedImage = (file: File): boolean =>
  EXTENSION_PATTERN.test(file.name) || /^image\//i.test(file.type);

/** Whether this browser can realistically compress and preview the file. */
export const isBrowserReadableImage = (file: File): boolean =>
  BROWSER_READABLE.test(file.type);
